/**
 * Direct upload engine — pushes files from the browser straight to S3 using
 * presigned URLs from /api/uploads/direct. Large files go up in parallel parts
 * with per-part retries; ETags are collected and reported for completion.
 */

import { api } from './api';
import type { DirectPlan, DirectPlanFile, DirectUploadKind } from './api/uploadApi';

export interface QueuedFile {
  id: string;
  file: File;
  /** Path relative to the dropped folder (or just the file name). */
  relativePath: string;
  size: number;
}

export type ItemStatus = 'queued' | 'uploading' | 'done' | 'error' | 'skipped' | 'cancelled';

export interface ItemState {
  id: string;
  relativePath: string;
  size: number;
  loaded: number;
  status: ItemStatus;
  key?: string;
  error?: string;
}

export interface BatchProgress {
  items: ItemState[];
  totalBytes: number;
  loadedBytes: number;
  done: number;
  failed: number;
  skipped: number;
  bytesPerSecond: number;
}

export interface BatchResult {
  plan: DirectPlan;
  items: ItemState[];
  uploadedKeys: string[];
}

interface Options {
  projectId: string;
  kind: DirectUploadKind;
  /** Parallel files for small uploads (default 4). */
  fileConcurrency?: number;
  /** Parallel parts within one large file (default 3). */
  partConcurrency?: number;
  onProgress?: (p: BatchProgress) => void;
  signal?: AbortSignal;
}

const PART_RETRIES = 4;

export function toQueuedFiles(fileList: FileList | File[]): QueuedFile[] {
  return Array.from(fileList).map((f, i) => ({
    id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
    file: f,
    relativePath: ((f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name).replace(/\\/g, '/'),
    size: f.size,
  }));
}

/** PUT a blob with progress via XHR; resolves with the ETag header (multipart parts need it). */
function putWithProgress(url: string, body: Blob, contentType: string | undefined, onLoaded: (n: number) => void, signal?: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onLoaded(e.loaded); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag') || '');
      else reject(new Error(`S3 responded ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    xhr.send(body);
  });
}

async function runPool<T>(items: T[], concurrency: number, worker: (item: T, index: number) => Promise<void>): Promise<void> {
  let next = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      await worker(items[i], i);
    }
  });
  await Promise.all(runners);
}

/**
 * Upload a batch of files for one project/kind. Resolves when every file has
 * either uploaded, failed, or been skipped by the server's plan.
 */
export async function uploadBatch(queued: QueuedFile[], opts: Options): Promise<BatchResult> {
  const { projectId, kind, fileConcurrency = 4, partConcurrency = 3, onProgress, signal } = opts;

  const items: ItemState[] = queued.map((q) => ({ id: q.id, relativePath: q.relativePath, size: q.size, loaded: 0, status: 'queued' }));
  const byId = new Map(items.map((it) => [it.id, it]));
  const totalBytes = queued.reduce((s, q) => s + q.size, 0);
  const startedAt = Date.now();
  let lastEmit = 0;

  const emit = (force = false) => {
    if (!onProgress) return;
    const now = Date.now();
    if (!force && now - lastEmit < 120) return;
    lastEmit = now;
    const loadedBytes = items.reduce((s, it) => s + (it.status === 'done' ? it.size : it.loaded), 0);
    const elapsed = Math.max(0.5, (now - startedAt) / 1000);
    onProgress({
      items: items.map((it) => ({ ...it })),
      totalBytes,
      loadedBytes,
      done: items.filter((it) => it.status === 'done').length,
      failed: items.filter((it) => it.status === 'error').length,
      skipped: items.filter((it) => it.status === 'skipped').length,
      bytesPerSecond: loadedBytes / elapsed,
    });
  };

  // 1. Ask the server where every file goes
  const plan = await api.upload.directPlan({
    projectId, kind,
    files: queued.map((q) => ({ relativePath: q.relativePath, size: q.size, contentType: q.file.type || 'application/octet-stream' })),
  });
  const planByPath = new Map<string, DirectPlanFile>(plan.files.map((f) => [f.relativePath, f]));

  for (const q of queued) {
    const pf = planByPath.get(q.relativePath.replace(/^\/+/, ''));
    const it = byId.get(q.id)!;
    if (!pf || pf.error) { it.status = 'skipped'; it.error = pf?.error || 'Not planned'; }
    else it.key = pf.key;
  }
  emit(true);

  const uploadedKeys: string[] = [];

  // 2. Upload
  const work = queued.filter((q) => byId.get(q.id)!.status === 'queued');
  await runPool(work, fileConcurrency, async (q) => {
    const it = byId.get(q.id)!;
    const pf = planByPath.get(q.relativePath.replace(/^\/+/, ''))!;
    if (signal?.aborted) { it.status = 'cancelled'; return; }
    it.status = 'uploading';
    emit();
    try {
      if (!pf.multipart) {
        await putWithProgress(pf.url!, q.file, pf.contentType, (n) => { it.loaded = n; emit(); }, signal);
      } else {
        await uploadMultipart(q, pf, partConcurrency, (n) => { it.loaded = n; emit(); }, signal);
      }
      it.loaded = it.size;
      it.status = 'done';
      if (it.key) uploadedKeys.push(it.key);
    } catch (err: any) {
      if (err?.name === 'AbortError' || signal?.aborted) {
        it.status = 'cancelled';
        if (pf.multipart && pf.uploadId && pf.key) api.upload.directAbort(pf.key, pf.uploadId).catch(() => {});
      } else {
        it.status = 'error';
        it.error = err?.message || 'Upload failed';
        if (pf.multipart && pf.uploadId && pf.key) api.upload.directAbort(pf.key, pf.uploadId).catch(() => {});
      }
    }
    emit(true);
  });

  emit(true);
  return { plan, items, uploadedKeys };
}

async function uploadMultipart(q: QueuedFile, pf: DirectPlanFile, partConcurrency: number, onLoaded: (n: number) => void, signal?: AbortSignal): Promise<void> {
  const partSize = pf.partSize!;
  const partCount = pf.partCount!;
  const partLoaded = new Array<number>(partCount).fill(0);
  const etags: Array<{ PartNumber: number; ETag: string }> = [];
  const report = () => onLoaded(partLoaded.reduce((s, n) => s + n, 0));

  const partIndexes = Array.from({ length: partCount }, (_, i) => i);
  await runPool(partIndexes, partConcurrency, async (i) => {
    const partNumber = i + 1;
    const start = i * partSize;
    const end = Math.min(q.size, start + partSize);
    const blob = q.file.slice(start, end);
    let attempt = 0;
    for (;;) {
      if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');
      try {
        const { url } = await api.upload.directPartUrl(pf.key!, pf.uploadId!, partNumber);
        const etag = await putWithProgress(url, blob, undefined, (n) => { partLoaded[i] = n; report(); }, signal);
        if (!etag) throw new Error('S3 did not return an ETag for the part (check bucket CORS ExposeHeaders)');
        partLoaded[i] = end - start;
        report();
        etags.push({ PartNumber: partNumber, ETag: etag });
        return;
      } catch (err: any) {
        if (err?.name === 'AbortError') throw err;
        attempt++;
        if (attempt > PART_RETRIES) throw err;
        partLoaded[i] = 0;
        report();
        await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1)));
      }
    }
  });

  await api.upload.directComplete(pf.key!, pf.uploadId!, etags);
}

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / Math.pow(k, i)).toFixed(i >= 3 ? 2 : 1)} ${units[i]}`;
}

export function formatRate(bytesPerSecond: number): string {
  return `${formatBytes(bytesPerSecond)}/s`;
}

export function formatEta(remainingBytes: number, bytesPerSecond: number): string {
  if (!bytesPerSecond || remainingBytes <= 0) return '';
  const s = Math.round(remainingBytes / bytesPerSecond);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}
